import { useEffect, useMemo, useState } from "react";
import Masonry from "./Masonry.jsx";
import Lightbox from "./Lightbox.jsx";
import Upload from "./Upload.jsx";
import { R2_ENABLED } from "../config.js";
import { driveFolderUrl } from "../utils/drive.js";

const CN_MONTH = ["一", "二", "三", "四", "五", "六", "七", "八", "九", "十", "十一", "十二"];

function photoTime(p) {
  return new Date(p.exifDate || p.uploadedAt || 0).getTime();
}
function monthLabel(p) {
  const d = new Date(p.exifDate || p.uploadedAt || 0);
  return `${d.getFullYear()} · ${CN_MONTH[d.getMonth()]}月`;
}

export default function Moments({ collection, photos, onClose, onUploaded }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  // 新的在前；同月份照片接連在一起
  const sorted = useMemo(
    () => [...(photos || [])].sort((a, b) => photoTime(b) - photoTime(a)),
    [photos],
  );

  // 依月份切組（sorted 已排好，同月份連續）
  const groups = useMemo(() => {
    const out = [];
    let cur = null;
    sorted.forEach((p, i) => {
      const label = monthLabel(p);
      if (!cur || cur.label !== label) {
        cur = { label, items: [], base: i };
        out.push(cur);
      }
      cur.items.push(p);
    });
    return out;
  }, [sorted]);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && lightboxIdx === null) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, lightboxIdx]);

  const lightboxPhoto = lightboxIdx !== null ? sorted[lightboxIdx] : null;
  const driveUrl = driveFolderUrl(collection.driveFolderId);

  function handleUploadClick() {
    if (!R2_ENABLED) { alert("Worker 設定尚未就緒，請聯絡管理員。"); return; }
    setUploadOpen(true);
  }

  return (
    <div className="det-overlay">
      <div className="det-top">
        <button className="det-back" onClick={onClose}>← 回活動索引</button>
        <div className="det-top-actions">
          <button className="det-action-btn" onClick={handleUploadClick} disabled={!R2_ENABLED} title={R2_ENABLED ? "上傳照片到散影" : "Worker 設定尚未就緒"}>
            ↑ 上傳照片
          </button>
          <div className="det-top-brand">E118</div>
        </div>
      </div>

      <div className="det-container">
        <section className="mom-hero">
          <div className="mom-eyebrow">
            <span>散影</span><span className="rule"></span><span>Moments</span>
          </div>
          <h1>{collection.title}</h1>
          <div className="mom-summary">
            {(collection.summary || "").split("\n").map((line, i) => (
              <span key={i}>{line}<br /></span>
            ))}
          </div>
          {collection.sub && <div className="mom-sub">{collection.sub}</div>}
          {driveUrl && (
            <div className="det-drive" style={{ marginTop: 20, paddingTop: 18 }}>
              <a className="det-drive-btn" href={driveUrl} target="_blank" rel="noopener noreferrer">
                查看散影完整資料夾（Google 雲端硬碟）→
              </a>
              <p className="det-drive-note">
                下方僅顯示已透過本站「上傳照片」功能加入的照片。若要看同學直接放進雲端硬碟資料夾裡、尚未同步到本站的照片，請點上方連結（僅限受邀同學／登入 Google 帳號檢視）。
              </p>
            </div>
          )}
        </section>

        {sorted.length === 0 ? (
          <div className="det-gallery-empty">
            照片陸續上傳中．不分場次的零碎照片都收在這，刷新即可看到新進的照片。
          </div>
        ) : (
          <section className="mom-body">
            {groups.map((g) => (
              <div key={g.label}>
                <div className="mom-div">
                  <span className="ml">{g.label}</span>
                  <span className="mline"></span>
                  <span className="mc">{g.items.length} 張</span>
                </div>
                <Masonry
                  photos={g.items}
                  indexBase={g.base}
                  onItemClick={(gi) => setLightboxIdx(gi)}
                />
              </div>
            ))}
          </section>
        )}

        <nav className="det-nav">
          <button onClick={onClose}>
            <span className="arrow">← Index</span>
            <span className="name">回活動索引</span>
          </button>
        </nav>
      </div>

      {lightboxPhoto && (
        <Lightbox
          photo={lightboxPhoto}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx((i) => Math.max(0, i - 1))}
          onNext={() => setLightboxIdx((i) => Math.min(sorted.length - 1, i + 1))}
          hasPrev={lightboxIdx > 0}
          hasNext={lightboxIdx < sorted.length - 1}
        />
      )}

      <Upload
        activity={collection}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={onUploaded}
      />
    </div>
  );
}
